import { QueueListIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import type { ColumnDef, ExpandedState } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { Dispatch, SetStateAction } from 'react';
import type { AmountUnit } from '@/shared/lib/amount/convertAmount';
import { convertAmount } from '@/shared/lib/amount/convertAmount';
import { formatAmount } from '@/shared/lib/format';
import {
  Button,
  DropdownMenu,
  getExpandedValue,
  removeExpandedValues,
  setExpandedValue,
  TableExpandButton,
  Text,
  WIDTH_PRESETS,
} from '@/shared/ui';
import { AssessmentNameCellWithTags } from '../components/AssessmentNameCellWithTags';
import type { AssessmentTableRowData } from './formatted-assessment-table-data';

export const ASSESSMENT_TABLE_NAME_COLUMN_WIDTH = 376;
export const FIRST_COLUMN_WIDTH = ASSESSMENT_TABLE_NAME_COLUMN_WIDTH + WIDTH_PRESETS.xxs;

type AssessmentColumnsProps = {
  /** 表示モード */
  onOpenAssessmentComparisonMenu: ((row: AssessmentTableRowData) => void) | undefined;
  /** 基準年度 */
  baseFiscalYear: number | undefined;
  /** 比較年度 */
  comparedFiscalYear: number | undefined;
  /** 金額単位 */
  amountUnit: AmountUnit;
  /** テーブルデータ */
  data: AssessmentTableRowData[];
  /** 展開状態 */
  expanded: ExpandedState | undefined;
  /** 展開状態セッター */
  setExpanded: Dispatch<SetStateAction<ExpandedState | undefined>>;
  /** 文言の翻訳 */
  t: TFunction<'translation'>;
  /** 全行IDを取得するヘルパー関数 */
  getRowIds: ({
    rows,
    includeAccumulations,
  }: {
    rows: AssessmentTableRowData[];
    includeAccumulations: boolean;
  }) => string[];
};

/**
 * 査定比較テーブルのカラム定義を生成
 */
export const createAssessmentTableColumns = ({
  onOpenAssessmentComparisonMenu,
  baseFiscalYear,
  comparedFiscalYear,
  amountUnit,
  data,
  expanded,
  setExpanded,
  t,
  getRowIds,
}: AssessmentColumnsProps): ColumnDef<AssessmentTableRowData>[] => {
  const allRowIds = getRowIds({ rows: data, includeAccumulations: true });
  const allLevel1Ids = getRowIds({ rows: data, includeAccumulations: false });

  const enabledDropdownMenu = baseFiscalYear && comparedFiscalYear;

  return [
    {
      accessorKey: 'expand',
      header: () => {
        return (
          <DropdownMenu
            label={t('common.table.expand.label')}
            contentAlign="start"
            disabled={!enabledDropdownMenu}
            menuItems={[
              {
                key: 'expand-to-project',
                item: t('common.table.expand.expand-to-project'),
                onClick: () => {
                  // 節・細節まで展開：全てのlevel1ExpandedIdsを展開
                  setExpanded(() => setExpandedValue({}, allLevel1Ids, true));
                },
              },
              {
                key: 'expand-all',
                item: t('common.table.expand.expand-all'),
                onClick: () => {
                  // すべて展開
                  setExpanded(() => setExpandedValue({}, allRowIds, true));
                },
                hasBottomSeparator: true,
              },
              {
                key: 'collapse-all',
                item: t('common.table.expand.collapse-all'),
                onClick: () => {
                  // すべて折りたたむ
                  setExpanded({});
                },
              },
            ]}
          >
            <Button variant="ghost" size="icon">
              <QueueListIcon />
            </Button>
          </DropdownMenu>
        );
      },
      meta: { width: 'xxs' },
      cell: ({ row }) => {
        const showExpandButton = row.original.level === 1;
        if (!showExpandButton) {
          return null;
        }

        const isExpanded = getExpandedValue(expanded, row.id);

        /**
         * level1の行をクリックした際に、全子孫行（level2, level3）を展開/折りたたみ
         */
        const handleExpandedChange = (expanded: ExpandedState) => {
          if (Object.keys(expanded).length === 0) {
            // 折りたたみの場合は、level1ExpandedIdsだけを削除（他の展開状態は維持）
            setExpanded((prev) => removeExpandedValues(prev, row.original.level1ExpandedIds || []));
            return;
          }
          // 展開の場合は、level1ExpandedIdsを全て展開
          setExpanded((prev) => setExpandedValue(prev, row.original.level1ExpandedIds || [], true));
        };

        return (
          <TableExpandButton
            onExpandedStateChange={handleExpandedChange}
            row={row}
            isExpanded={isExpanded}
          />
        );
      },
    },
    {
      accessorKey: 'name',
      header: t('assessment.fields.code-name'),
      meta: { width: ASSESSMENT_TABLE_NAME_COLUMN_WIDTH },
      cell: ({ row }) => {
        const showExpandButton = !!row.original.accumulation;
        const paddingLeft = (row.original.level - 2) * WIDTH_PRESETS.xxs;
        return (
          <div style={{ paddingLeft: paddingLeft }} className="flex w-full items-center">
            {showExpandButton && (
              <TableExpandButton
                row={row}
                canExpand={true}
                isExpanded={getExpandedValue(expanded, row.id)}
                onExpandedChange={(expandedState) => {
                  setExpanded((prev) => setExpandedValue(prev, [row.id], expandedState));
                }}
              />
            )}
            <div className="flex items-center gap-2">
              {!!row.original.accumulation?.mappings.length && (
                <div className="ml-1">
                  <SparklesIcon className="h-4 w-4 text-text-accent" />
                </div>
              )}

              {showExpandButton ? (
                <AssessmentNameCellWithTags
                  row={row.original}
                  onClick={
                    onOpenAssessmentComparisonMenu
                      ? () => onOpenAssessmentComparisonMenu(row.original)
                      : undefined
                  }
                />
              ) : (
                <Text>{row.original.name}</Text>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'base_forecast',
      header: t('assessment.comparison-table.budget-amount', {
        year: baseFiscalYear,
      }),
      meta: { width: 'auto', minWidth: 'l' }, // 均等配置
      cell: ({ row: { original } }) => {
        return (
          <Text viewMode="number" unit={amountUnit} className="w-full text-right">
            {original.baseAmount}
          </Text>
        );
      },
    },
    {
      id: 'compared_forecast',
      header: t('assessment.comparison-table.budget-amount', {
        year: comparedFiscalYear,
      }),
      meta: { width: 'auto', minWidth: 'l' }, // 均等配置
      cell: ({ row: { original } }) => {
        return (
          <Text viewMode="number" unit={amountUnit} className="w-full text-right">
            {original.comparedAmount}
          </Text>
        );
      },
    },
    {
      id: 'difference',
      header: t('assessment.fields.difference'),
      meta: { width: 'auto', minWidth: 'l' }, // 均等配置
      cell: ({ row: { original } }) => {
        const difference = original.differenceAmount;
        const convertedDifference = convertAmount(Math.abs(difference), amountUnit) ?? 0;
        const unitKey =
          amountUnit === 'thousand' ? 'common.unit.amount-thousand-yen' : 'common.unit.amount-yen';
        const formattedDifference = t(unitKey, {
          amount: formatAmount(convertedDifference),
        });
        const isNegative = difference < 0;
        const isZero = convertedDifference === 0;
        const amount = isZero
          ? '0'
          : isNegative
            ? `-${formattedDifference}`
            : `+${formattedDifference}`;
        return (
          <Text
            color={isZero ? 'base' : isNegative ? 'success' : 'error'}
            className={'w-full text-right'}
          >
            {amount}
          </Text>
        );
      },
    },
  ];
};
